// Largest-remainder allocation in CENTS — the one rule that splits a Surcharge /
// Agency Commission block total across the rows that carry it.
//
// Plain proportional division leaves a residue, and the residue is not cosmetic.
// The Excel workbook's Totals cell is a live `SUM()` over the data rows while
// its cached value is the engine total, so a single stray cent prints two
// different numbers on one quote. The Customer Preview has the same problem in a
// different skin: the cards are the quote, and they must add up to the total
// printed underneath them.
//
// Guarantees: every share is a whole number of cents, and the shares sum to
// EXACTLY `total`. A zero total weight (every row priced 0) splits evenly rather
// than dividing by zero.
//
// Extracted from the XT-531 export distributor (XT-596) so the workbook and the
// Customer Preview cannot round differently.
export const allocateByWeight = (
  total: number,
  weights: readonly number[]
): number[] => {
  if (weights.length === 0) return [];

  const totalCents = Math.round(total * 100);
  if (totalCents === 0) return weights.map(() => 0);
  const safeWeights = weights.map((weight) => (weight > 0 ? weight : 0));
  const weightSum = safeWeights.reduce((sum, weight) => sum + weight, 0);
  const effectiveWeights =
    weightSum > 0 ? safeWeights : safeWeights.map(() => 1);
  const effectiveSum = weightSum > 0 ? weightSum : effectiveWeights.length;

  const exact = effectiveWeights.map(
    (weight) => (totalCents * weight) / effectiveSum
  );
  const floors = exact.map((value) => Math.floor(value));
  let residue = totalCents - floors.reduce((sum, value) => sum + value, 0);

  // Hand the leftover cents to the largest fractional parts first; ties go to
  // the earlier row so the result is deterministic across runs.
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const cents = [...floors];
  for (const entry of order) {
    if (residue <= 0) break;
    cents[entry.index] = (cents[entry.index] ?? 0) + 1;
    residue -= 1;
  }

  return cents.map((value) => value / 100);
};
